/**
 * InvestigationGroupsSection - Displays investigations in the sidebar grouped by preference
 *
 * Refactored to use the generic SidebarResourceSection component.
 * Note: Investigations don't support folders, so we use basic group rendering.
 */

import React, { useCallback } from "react";
import { FaProjectDiagram } from "react-icons/fa";
import { Investigation } from "@/models/investigation";
import { useInvestigationsStore } from "@/hooks/use-investigation-store";
import { useInvestigationGroupBy } from "@/hooks/use-resource-group-by";
import { investigationGalleryConfig } from "@/config/resource-gallery-configs";
import { investigationSectionConfig } from "@/config/sidebar-section-configs";
import useNotif from "@/hooks/use-notif";
import SidebarResourceSection, {
  SidebarGroupRenderProps,
} from "./sidebar-resource-section";
import SidebarGroupHeader, { SidebarGroupContent } from "./sidebar-group-header";
import InvestigationItem from "./investigation-item";
import { useMultiSelect } from "@/hooks/use-multi-select";

// =============================================================================
// Types
// =============================================================================

interface InvestigationGroupsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

// =============================================================================
// Component
// =============================================================================

const InvestigationGroupsSection: React.FC<InvestigationGroupsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const { addSuccess, addError } = useNotif();

  // Multi-select hook
  const { isSelected } = useMultiSelect();

  // Fetch investigations via store
  const { resources: investigations, remove: removeInvestigation } = useInvestigationsStore();

  // Get groupBy preference from shared hook
  const { groupBy } = useInvestigationGroupBy();

  // Handle delete investigation
  const handleDelete = useCallback(
    async (investigation: Investigation) => {
      try {
        await removeInvestigation(investigation.id);
        addSuccess("Investigation supprimée");
      } catch (error) {
        addError("Erreur lors de la suppression");
      }
    },
    [removeInvestigation, addSuccess, addError]
  );

  // Render simple item
  const renderItem = useCallback(
    (props: {
      item: Investigation;
      isSelected: boolean;
      orderedIds: string[];
      onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
    }) => (
      <InvestigationItem
        key={props.item.id}
        investigation={props.item}
        onDelete={handleDelete}
        isSelected={props.isSelected}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [handleDelete]
  );

  // Custom group header with investigation icons
  const renderGroupHeader = useCallback(
    (props: SidebarGroupRenderProps) => {
      const icon = investigationSectionConfig.getGroupIcon(props.groupKey, groupBy, []);
      return (
        <SidebarGroupHeader
          groupKey={props.groupKey}
          icon={icon}
          count={props.itemCount}
          isExpanded={!props.isCollapsed}
          onToggle={props.onToggle}
          showCreateFolder={false}
        />
      );
    },
    [groupBy]
  );

  return (
    <SidebarResourceSection<Investigation>
      items={investigations}
      config={investigationGalleryConfig}
      renderItem={renderItem}
      renderGroupHeader={renderGroupHeader}
      emptyMessage={investigationSectionConfig.emptyMessage}
      emptyIcon={<FaProjectDiagram />}
      noResultsMessage={investigationSectionConfig.noResultsMessage}
      searchPlaceholder={investigationSectionConfig.searchPlaceholder}
      onMultiSelectContextMenu={onMultiSelectContextMenu}
      externalGroupBy={groupBy}
    />
  );
};

InvestigationGroupsSection.displayName = "InvestigationGroupsSection";

export default InvestigationGroupsSection;
